/**
 * StackedAreaChart — Recharts-backed multi-series stacked area chart
 * for cumulative neutralization and capacity readouts.
 *
 * Complements the existing line and bar chart family by offering the
 * canonical "share of total over time" surface — useful when a demo
 * wants to communicate not just "this metric trended up" but also
 * "here is the per-theorem decomposition of the rise".
 *
 * Single responsibility: render the stacked surface. The host owns
 * the data shape; this chart picks token-aligned fills, applies
 * stacked area math through Recharts, and respects the demo-suite's
 * responsive container conventions.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React from "react";
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { GRAY } from "../../design-system/tokens.js";
import { DEFAULT_CHART_PALETTE, type ChartPalette, theoremSeriesColor } from "./ChartTheme.js";

export interface StackedAreaSeriesDefinition {
  /** Series key matching a property in each data row. */
  readonly key: string;
  /** Display name for the legend. */
  readonly label: string;
  /** Optional canonical theorem name for token-aligned coloring. */
  readonly theorem?: string;
  /** Optional explicit color override. */
  readonly color?: string;
}

export interface StackedAreaChartProps {
  /** Time-ordered data rows. Each row has `x` plus one numeric field per series. */
  readonly data: readonly Readonly<
    { readonly x: string | number } & Record<string, number | string>
  >[];
  /** Series definitions, ordered bottom-to-top in the stack. */
  readonly series: readonly StackedAreaSeriesDefinition[];
  /** Pixel height. Defaults to 220. */
  readonly height?: number;
  /** Whether the chart shows its legend. Defaults to true. */
  readonly showGrid?: boolean;
  /** Accessible label for the surrounding figure. */
  readonly ariaLabel: string;
  /** Optional X-axis tick formatter. */
  readonly formatX?: (value: string | number) => string;
  /** Optional Y-axis tick formatter. */
  readonly formatY?: (value: number) => string;
  /** Palette override. */
  readonly palette?: ChartPalette;
}

function resolveStroke(series: StackedAreaSeriesDefinition, palette: ChartPalette): string {
  if (series.color !== undefined) return series.color;
  if (series.theorem !== undefined) return theoremSeriesColor(series.theorem, palette);
  return palette.primary;
}

const DEFAULT_RESPONSIVE_WIDTH = 640;

/** Renders a token-aligned stacked area chart. */
export const StackedAreaChart = React.memo(function StackedAreaChart({
  data,
  series,
  height = 220,
  showGrid = true,
  ariaLabel,
  formatX,
  formatY,
  palette = DEFAULT_CHART_PALETTE,
}: StackedAreaChartProps): React.JSX.Element {
  const mutableData = React.useMemo(() => data.map((row) => ({ ...row })), [data]);
  // Per-instance prefix so multiple charts on one page do not collide on
  // their `<linearGradient id=…>` definitions, which would otherwise route
  // `fill=url(#id)` references to whichever chart mounted first.
  const reactId = React.useId();
  const gradientIdFor = React.useCallback(
    (key: string) => `csk-stack-${reactId.replace(/[^A-Za-z0-9_-]/g, "")}-${key}`,
    [reactId],
  );

  return (
    <figure
      role="img"
      aria-label={ariaLabel}
      data-csk-chart="stacked-area"
      style={{ margin: 0, width: "100%", height }}
    >
      <ResponsiveContainer
        width="100%"
        height="100%"
        minWidth={0}
        minHeight={height}
        initialDimension={{ width: DEFAULT_RESPONSIVE_WIDTH, height }}
      >
        <AreaChart data={mutableData} margin={{ top: 12, right: 12, left: 0, bottom: 8 }}>
          <defs>
            {series.map((definition) => {
              const stroke = resolveStroke(definition, palette);
              const gradientId = gradientIdFor(definition.key);
              return (
                <linearGradient key={definition.key} id={gradientId} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={stroke} stopOpacity={0.55} />
                  <stop offset="100%" stopColor={stroke} stopOpacity={0.08} />
                </linearGradient>
              );
            })}
          </defs>
          {showGrid && <CartesianGrid stroke={GRAY[300]} strokeDasharray="3 3" />}
          <XAxis
            dataKey="x"
            stroke={GRAY[500]}
            fontSize={11}
            {...(formatX !== undefined
              ? { tickFormatter: (value: string | number) => formatX(value) }
              : {})}
          />
          <YAxis
            stroke={GRAY[500]}
            fontSize={11}
            {...(formatY !== undefined ? { tickFormatter: (value: number) => formatY(value) } : {})}
          />
          <Tooltip
            contentStyle={{
              background: "white",
              border: `1px solid ${GRAY[300]}`,
              borderRadius: 6,
              color: GRAY[900],
              fontSize: 12,
            }}
            labelStyle={{ color: GRAY[700], fontWeight: 600 }}
          />
          {series.map((definition) => {
            const stroke = resolveStroke(definition, palette);
            return (
              <Area
                key={definition.key}
                type="monotone"
                dataKey={definition.key}
                name={definition.label}
                stackId="stack"
                stroke={stroke}
                fill={`url(#${gradientIdFor(definition.key)})`}
                strokeWidth={1.5}
                isAnimationActive
              />
            );
          })}
        </AreaChart>
      </ResponsiveContainer>
    </figure>
  );
});
